import React, { createContext, useContext, useReducer, useEffect, ReactNode } from 'react';
import { User } from '@avanzu/types';
import { apiClient } from '../services/apiClient';

// Simplified state shape - no organization management needed for admin
interface AuthState {
  user: User | null;
  loading: boolean;
}

enum AuthActionTypes {
  SET_LOADING = 'SET_LOADING',
  SET_USER = 'SET_USER',
  CLEAR_AUTH = 'CLEAR_AUTH',
  UPDATE_USER = 'UPDATE_USER',
}

// Action types
type AuthAction =
  | { type: AuthActionTypes.SET_LOADING; payload: boolean }
  | { type: AuthActionTypes.SET_USER; payload: User }
  | { type: AuthActionTypes.CLEAR_AUTH }
  | { type: AuthActionTypes.UPDATE_USER; payload: Partial<User> };

// Reducer for cleaner state management
function authReducer(state: AuthState, action: AuthAction): AuthState {
  switch (action.type) {
    case AuthActionTypes.SET_LOADING:
      return { ...state, loading: action.payload };

    case AuthActionTypes.SET_USER:
      return {
        ...state,
        user: action.payload,
        loading: false,
      };

    case AuthActionTypes.UPDATE_USER:
      if (!state.user) return state;
      return {
        ...state,
        user: { ...state.user, ...action.payload },
      };

    case AuthActionTypes.CLEAR_AUTH:
      return {
        user: null,
        loading: false,
      };

    default:
      return state;
  }
}

// Storage utilities
const storage = {
  save(user: User) {
    localStorage.setItem('user', JSON.stringify(user));
  },

  load(): User | null {
    try {
      const user = localStorage.getItem('user');
      if (!user) return null;
      return JSON.parse(user);
    } catch {
      return null;
    }
  },

  clear() {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    sessionStorage.clear();
  },
};

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: (token: string, user?: User) => Promise<void>;
  logout: () => Promise<void>;
  refreshUserState: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, {
    user: null,
    loading: true,
  });

  // Handle auth failures
  const handleAuthFailure = () => {
    storage.clear();
    dispatch({ type: AuthActionTypes.CLEAR_AUTH });
    window.location.href = '/login';
  };

  // Set up API client error handling
  useEffect(() => {
    apiClient.setAuthCallbacks({
      onAuthFailure: handleAuthFailure,
      onOrganisationInvalidated: () => {}, // Not needed for admin
    });
  }, []);

  // Load initial auth state — verify the session with the server before
  // trusting the cached user, so we don't flash the dashboard for a stale token.
  useEffect(() => {
    const token = localStorage.getItem('token');

    if (!token) {
      dispatch({ type: AuthActionTypes.SET_LOADING, payload: false });
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/user/profile`, {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        });

        if (cancelled) return;

        if (!response.ok) {
          storage.clear();
          dispatch({ type: AuthActionTypes.CLEAR_AUTH });
          return;
        }

        const data = await response.json();
        if (cancelled) return;
        storage.save(data.user);
        dispatch({ type: AuthActionTypes.SET_USER, payload: data.user });
      } catch {
        if (cancelled) return;
        storage.clear();
        dispatch({ type: AuthActionTypes.CLEAR_AUTH });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  const login = async (token: string, user?: User) => {
    localStorage.setItem('token', token);

    if (user) {
      storage.save(user);
      dispatch({
        type: AuthActionTypes.SET_USER,
        payload: user,
      });
    } else {
      // Just token provided - fetch user data
      await refreshUserState();
    }
  };

  const logout = async () => {
    try {
      await apiClient.post('/auth/logout');
    } catch (error) {
      console.error('Logout API error:', error);
    } finally {
      storage.clear();
      dispatch({ type: AuthActionTypes.CLEAR_AUTH });
    }
  };

  const refreshUserState = async () => {
    const token = localStorage.getItem('token');
    if (!token) return;

    try {
      const profileResponse = await apiClient.get('/user/profile');
      if (profileResponse.ok) {
        const profileData = await profileResponse.json();
        storage.save(profileData.user);
        dispatch({
          type: AuthActionTypes.SET_USER,
          payload: profileData.user,
        });
      }
    } catch (error) {
      console.error('Failed to refresh user state:', error);
      // Error handling is done by apiClient interceptor
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user: state.user,
        loading: state.loading,
        login,
        logout,
        refreshUserState,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
